import { Button } from "@shared-ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@shared-ui/card";
import { Input } from "@shared-ui/input";
import { Skeleton } from "@shared-ui/skeleton";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { EmptyState } from "@/components/ui/empty-state";
import { useAdminAuthStore } from "@/features/auth/store/admin-auth.store";
import { DataTable } from "@/features/catalog/components/data-table";
import {
	GstRateDialog,
	type GstRateDialogMode,
} from "@/features/catalog/components/gst-rate-dialog";
import { GstSignOffDialog } from "@/features/catalog/components/gst-sign-off-dialog";
import {
	formatRate,
	formatRateDay,
	formatSignOffDay,
	GST_WRITE_PERMISSION,
	STATE_LABEL,
	STATE_VARIANT,
} from "@/features/catalog/lib/gst-rates";
import {
	type GstRate,
	type GstRateState,
	listGstRates,
} from "@/features/catalog/services/gst-rates-api.service";
import { hasPermission } from "@/lib/permissions";
import { useDebounce } from "@/lib/use-debounce";

/**
 * Catalogue > GST rates (#514): every rate row, newest first within a prefix,
 * filtered to what is in force and scheduled unless asked otherwise.
 *
 * Adding a rate starts here; cancelling and correcting happen on a prefix's
 * own page, where the row's neighbours are in view.
 */

const ALL_STATES: GstRateState[] = [
	"in_force",
	"scheduled",
	"closed",
	"withdrawn",
];
const DEFAULT_STATES: GstRateState[] = ["in_force", "scheduled"];

export const GST_RATES_KEY = ["admin", "catalog", "gst-rates"] as const;

export function SignOffCell({
	rate,
	canWrite,
	onRecord,
}: {
	rate: GstRate;
	canWrite: boolean;
	onRecord: (rate: GstRate) => void;
}) {
	if (rate.signedOffBy) {
		return (
			<span>
				{formatSignOffDay(rate.signedOffOn)}, {rate.signedOffBy}
				{rate.signedOffRecordedBy ? (
					<span className="text-muted-foreground">
						{" "}
						(recorded by {rate.signedOffRecordedBy})
					</span>
				) : null}
			</span>
		);
	}
	return (
		<span className="grid justify-items-start gap-1">
			<span className="text-amber-800">Not signed off</span>
			{canWrite && rate.origin === "seed" && rate.state !== "withdrawn" ? (
				<Button
					variant="outline"
					size="sm"
					onClick={() => onRecord(rate)}
					aria-label={`Record sign-off for ${rate.hsnPrefix} from ${formatRateDay(rate.effectiveFrom)}`}
				>
					Record sign-off
				</Button>
			) : null}
		</span>
	);
}

export function CatalogGstRatesPage() {
	const queryClient = useQueryClient();
	const operator = useAdminAuthStore((state) => state.operator);
	const canWrite = hasPermission(operator, GST_WRITE_PERMISSION);
	const [searchParams, setSearchParams] = useSearchParams();
	const [dialog, setDialog] = useState<GstRateDialogMode | null>(null);
	const [signing, setSigning] = useState<GstRate | null>(null);

	const prefixParam = searchParams.get("prefix") ?? "";
	const [prefixInput, setPrefixInput] = useState(prefixParam);
	const prefix = useDebounce(prefixInput.replace(/[^0-9]/g, ""), 250);
	const stateParams = searchParams.getAll("state") as GstRateState[];
	const states = stateParams.length > 0 ? stateParams : DEFAULT_STATES;
	const page = Number(searchParams.get("page") ?? "1") || 1;

	const rates = useQuery({
		queryKey: [...GST_RATES_KEY, "list", prefix, states.join(","), page],
		queryFn: () => listGstRates({ prefix, states, page }),
		placeholderData: (previous) => previous,
	});

	const update = (next: {
		prefix?: string;
		states?: GstRateState[];
		page?: number;
	}) => {
		const params = new URLSearchParams();
		const nextPrefix = next.prefix ?? prefix;
		if (nextPrefix) params.set("prefix", nextPrefix);
		for (const state of next.states ?? states) params.append("state", state);
		if ((next.page ?? 1) > 1) params.set("page", String(next.page));
		setSearchParams(params);
	};

	const toggleState = (state: GstRateState) => {
		const next = states.includes(state)
			? states.filter((one) => one !== state)
			: [...states, state];
		update({ states: next.length > 0 ? next : DEFAULT_STATES, page: 1 });
	};

	const refresh = () =>
		queryClient.invalidateQueries({ queryKey: [...GST_RATES_KEY] });

	const rows = rates.data?.items ?? [];
	const total = rates.data?.total ?? 0;
	const pageSize = rates.data?.pageSize ?? 50;

	return (
		<div className="grid gap-4">
			<Card>
				<CardHeader className="flex flex-row flex-wrap items-start justify-between gap-4">
					<div className="grid max-w-2xl gap-1.5">
						<CardTitle>GST rates</CardTitle>
						<CardDescription>
							The GST rate for each HSN or SAC prefix, by date. A product takes
							the rate of the longest prefix of its code that is in force on its
							price's date.
						</CardDescription>
					</div>
					{canWrite ? (
						<Button onClick={() => setDialog({ kind: "add" })}>
							Add a rate
						</Button>
					) : null}
				</CardHeader>
				<CardContent className="grid gap-4">
					<div className="flex flex-wrap items-end gap-4">
						<div className="grid gap-1">
							<label
								htmlFor="gst-filter-prefix"
								className="text-sm font-medium"
							>
								Prefix
							</label>
							<Input
								id="gst-filter-prefix"
								className="w-40"
								inputMode="numeric"
								value={prefixInput}
								onChange={(event) => {
									setPrefixInput(event.target.value);
									if (page > 1) update({ page: 1 });
								}}
								placeholder="e.g. 25"
							/>
						</div>
						<fieldset className="flex flex-wrap gap-x-4 gap-y-2 text-sm">
							<legend className="sr-only">Show rates that are</legend>
							{ALL_STATES.map((state) => (
								<label key={state} className="flex items-center gap-2">
									<input
										type="checkbox"
										checked={states.includes(state)}
										onChange={() => toggleState(state)}
									/>
									{STATE_LABEL[state]}
								</label>
							))}
						</fieldset>
					</div>

					{rates.isPending ? (
						<div className="grid gap-2">
							<Skeleton className="h-9 w-full" />
							<Skeleton className="h-9 w-full" />
							<Skeleton className="h-9 w-full" />
						</div>
					) : rates.isError ? (
						<div className="grid gap-3 rounded-2xl border border-dashed p-6 text-center">
							<p className="text-sm text-muted-foreground">
								Could not load GST rates.
							</p>
							<div>
								<Button
									variant="outline"
									size="sm"
									onClick={() => rates.refetch()}
								>
									Try again
								</Button>
							</div>
						</div>
					) : rows.length === 0 ? (
						<EmptyState
							title={
								prefix
									? `No ${states.map((s) => STATE_LABEL[s].toLowerCase()).join(" or ")} rates for prefixes starting ${prefix}`
									: `No ${states.map((s) => STATE_LABEL[s].toLowerCase()).join(" or ")} rates`
							}
							hint="Change the prefix or tick more states to see other rates."
						/>
					) : (
						<>
							<ul className="grid gap-2 sm:hidden">
								{rows.map((rate) => (
									<li
										key={rate.id}
										className="flex items-start justify-between gap-3 rounded-2xl border p-3 text-sm"
									>
										<div className="grid gap-0.5">
											<Link
												className="font-mono font-semibold underline"
												to={`/catalog/gst-rates/${rate.hsnPrefix}`}
											>
												{rate.hsnPrefix}
											</Link>
											<span className="text-muted-foreground">
												from {formatRateDay(rate.effectiveFrom)}
											</span>
										</div>
										<div className="grid justify-items-end gap-1">
											<span className="font-semibold">
												{formatRate(rate.rateBp)}
											</span>
											<Badge variant={STATE_VARIANT[rate.state]}>
												{STATE_LABEL[rate.state]}
											</Badge>
										</div>
									</li>
								))}
							</ul>
							<div className="hidden sm:block">
								<DataTable
									rows={rows}
									rowKey={(rate) => rate.id}
									isRefreshing={rates.isFetching && !rates.isPending}
									columns={[
										{
											header: "Prefix",
											cell: (rate) => (
												<Link
													className="font-mono font-semibold underline"
													to={`/catalog/gst-rates/${rate.hsnPrefix}`}
												>
													{rate.hsnPrefix}
												</Link>
											),
										},
										{
											header: "Rate",
											align: "right",
											cellClassName: "tabular-nums",
											cell: (rate) => formatRate(rate.rateBp),
										},
										{
											header: "From",
											cell: (rate) => formatRateDay(rate.effectiveFrom),
										},
										{
											header: "To",
											cell: (rate) => formatRateDay(rate.effectiveTo),
										},
										{
											header: "State",
											cell: (rate) => (
												<Badge variant={STATE_VARIANT[rate.state]}>
													{STATE_LABEL[rate.state]}
												</Badge>
											),
										},
										{
											header: "Notification",
											cell: (rate) => (
												<span className="grid max-w-xs">
													<span className="truncate" title={rate.source}>
														{rate.source}
													</span>
													<span className="text-xs text-muted-foreground">
														{rate.notificationSerial ?? "S. No. not recorded"}
													</span>
												</span>
											),
										},
										{
											header: "Sign-off",
											cell: (rate) => (
												<SignOffCell
													rate={rate}
													canWrite={canWrite}
													onRecord={setSigning}
												/>
											),
										},
									]}
								/>
							</div>
							<div className="flex items-center justify-between text-sm text-muted-foreground">
								<span>
									{(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)}{" "}
									of {total}
								</span>
								<div className="flex gap-2">
									<Button
										variant="outline"
										size="sm"
										disabled={page <= 1}
										onClick={() => update({ page: page - 1 })}
									>
										Previous
									</Button>
									<Button
										variant="outline"
										size="sm"
										disabled={page * pageSize >= total}
										onClick={() => update({ page: page + 1 })}
									>
										Next
									</Button>
								</div>
							</div>
						</>
					)}
				</CardContent>
			</Card>

			{dialog ? (
				<GstRateDialog
					mode={dialog}
					onClose={() => setDialog(null)}
					onChanged={refresh}
				/>
			) : null}
			{signing ? (
				<GstSignOffDialog
					rate={signing}
					onClose={() => setSigning(null)}
					onChanged={refresh}
				/>
			) : null}
		</div>
	);
}
